import Button from '@pelckmans/elementis/components/button';
import List from '@pelckmans/elementis/components/list';
import ListGroupComponent from '@pelckmans/elementis/components/list-group';
import Pill from '@pelckmans/elementis/components/pill';
import { useToast } from '@pelckmans/elementis/components/toast';
import Tooltip from '@pelckmans/elementis/components/tooltip';
import FailureIcon from '@pelckmans/elementis/icons/failure';
import InfoIcon from '@pelckmans/elementis/icons/info';
import PendingIcon from '@pelckmans/elementis/icons/pending';
import SuccessIcon from '@pelckmans/elementis/icons/success';
import Trash from '@pelckmans/elementis/icons/trash';
import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Group, Member } from '../../../../../../api/groups';
import { TOAST_ID } from '../../../../../../constants/ToastIds';
import ModalContext from '../../../../../../contexts/ModalContext';
import { SchoolYear } from '../../../../context/MyGroupsContext';
import useGroups from '../../../../hooks/useGroups';
import InviteStudent from './InviteStudent';
import DeleteMemberModal from './modals/DeleteMemberModal';

type Props = {
  group: Group;
  members: Member[];
  schoolYear: SchoolYear;
  setMembers: (members: Member[]) => void;
};

function ListGroup({ group, members, schoolYear, setMembers }: Props) {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const { showModal } = useContext(ModalContext);
  const { removeStudentFromGroup } = useGroups();

  const acceptedMembers = useMemo(() => members.filter(member => member.status === 'accepted'), [members]);
  const pendingMembers = useMemo(() => members.filter(member => member.status === 'pending'), [members]);
  const declinedMembers = useMemo(() => members.filter(member => member.status === 'declined'), [members]);

  const handleRemoveStudentFromGroup = async (student: Member) => {
    if (!schoolYear) return;

    try {
      await removeStudentFromGroup(schoolYear, group.id, student);

      const nextMembers = members.filter(x => x.id !== student.id);
      setMembers(nextMembers);

      const title =
        student.status === 'accepted'
          ? t('PAGES.MY_GROUPS.MODALS.REMOVE_MEMBER_GROUP.ACCEPTED.TOAST.SUCCESS.TITLE')
          : t('PAGES.MY_GROUPS.MODALS.REMOVE_MEMBER_GROUP.INVITED.TOAST.SUCCESS.TITLE');
      const message =
        student.status === 'accepted'
          ? t('PAGES.MY_GROUPS.MODALS.REMOVE_MEMBER_GROUP.ACCEPTED.TOAST.SUCCESS.MESSAGE')
          : t('PAGES.MY_GROUPS.MODALS.REMOVE_MEMBER_GROUP.INVITED.TOAST.SUCCESS.MESSAGE');

      addToast({
        id: TOAST_ID.STUDENT_REMOVE_SUCCESS,
        variant: 'success',
        title,
        children: message,
      });
    } catch (err) {
      const title =
        student.status === 'accepted'
          ? t('PAGES.MY_GROUPS.MODALS.REMOVE_MEMBER_GROUP.ACCEPTED.TOAST.ERROR.TITLE')
          : t('PAGES.MY_GROUPS.MODALS.REMOVE_MEMBER_GROUP.INVITED.TOAST.ERROR.TITLE');
      const message =
        student.status === 'accepted'
          ? t('PAGES.MY_GROUPS.MODALS.REMOVE_MEMBER_GROUP.ACCEPTED.TOAST.ERROR.MESSAGE')
          : t('PAGES.MY_GROUPS.MODALS.REMOVE_MEMBER_GROUP.INVITED.TOAST.ERROR.MESSAGE');

      addToast({
        id: TOAST_ID.STUDENT_REMOVE_ERROR,
        variant: 'danger',
        title,
        children: message,
      });
    }
  };

  const handleShowDeleteMemberModal = (student: Member) => {
    showModal(
      <DeleteMemberModal id="delete-invite-modal" student={student} onConfirm={handleRemoveStudentFromGroup} />,
    );
  };

  return (
    <ListGroupComponent>
      <InviteStudent group={group} members={members} setMembers={setMembers} schoolYear={schoolYear} />
      <List density="compact">
        <List.Header
          title={t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.STUDENTS.BODY.LIST_GROUP.ACCEPTED.TITLE')}
          pill={
            <Pill icon={<SuccessIcon data-testid="success-icon" />} prominence="success" size="sm">
              {String(group.totalAccepted)}
            </Pill>
          }
          titleTag="h3"
          tooltip={
            <Tooltip anchor={<InfoIcon data-testid="tooltip-accepted" />} truncation="wrapped">
              <p>{t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.STUDENTS.BODY.LIST_GROUP.ACCEPTED.TOOLTIP_P1')}</p>
              <p>{t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.STUDENTS.BODY.LIST_GROUP.ACCEPTED.TOOLTIP_P2')}</p>
              <p>{t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.STUDENTS.BODY.LIST_GROUP.ACCEPTED.TOOLTIP_P3')}</p>
            </Tooltip>
          }
        />
        {acceptedMembers.length === 0 && (
          <List.Item title={t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.STUDENTS.BODY.LIST_GROUP.ACCEPTED.EMPTY')} />
        )}
        {acceptedMembers.map(member => (
          <List.Item
            key={member.id}
            title={[member.lastName, member.firstName].join(' ')}
            subText={member.primaryEmail}
            actionArea={
              <Button
                prominence="neutral"
                size="sm"
                aria-label={t('PAGES.MY_GROUPS.LIST_GROUP.STUDENTS.ACCEPTED.REMOVE.A11Y_LABEL')}
                onClick={() => handleShowDeleteMemberModal(member)}
                startIcon={<Trash size="sm" />}
              />
            }
          />
        ))}
      </List>

      <List density="compact">
        <List.Header
          title={t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.STUDENTS.BODY.LIST_GROUP.PENDING.TITLE')}
          pill={
            <Pill icon={<PendingIcon data-testid="pending-icon" />} prominence="warning" size="sm">
              {String(group.totalPending)}
            </Pill>
          }
          titleTag="h3"
          tooltip={
            <Tooltip anchor={<InfoIcon data-testid="tooltip-pending" />} truncation="wrapped">
              <p>{t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.STUDENTS.BODY.LIST_GROUP.PENDING.TOOLTIP_P1')}</p>
              <p>{t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.STUDENTS.BODY.LIST_GROUP.PENDING.TOOLTIP_P2')}</p>
            </Tooltip>
          }
        />
        {pendingMembers.length === 0 && (
          <List.Item title={t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.STUDENTS.BODY.LIST_GROUP.PENDING.EMPTY')} />
        )}
        {pendingMembers.map(member => (
          <List.Item
            key={member.id}
            title={member.primaryEmail}
            actionArea={
              <Button
                prominence="neutral"
                size="sm"
                aria-label={t('PAGES.MY_GROUPS.LIST_GROUP.STUDENTS.PENDING.REMOVE.A11Y_LABEL')}
                onClick={() => handleShowDeleteMemberModal(member)}
                startIcon={<Trash size="sm" />}
              />
            }
          />
        ))}
      </List>
      <List density="compact">
        <List.Header
          title={t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.STUDENTS.BODY.LIST_GROUP.DECLINED.TITLE')}
          pill={
            <Pill icon={<FailureIcon data-testid="failure-icon" />} prominence="danger" size="sm">
              {String(group.totalDeclined)}
            </Pill>
          }
          titleTag="h3"
          tooltip={
            <Tooltip anchor={<InfoIcon data-testid="tooltip-declined" />} truncation="wrapped">
              <p>{t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.STUDENTS.BODY.LIST_GROUP.DECLINED.TOOLTIP_P1')}</p>
              <p>{t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.STUDENTS.BODY.LIST_GROUP.DECLINED.TOOLTIP_P2')}</p>
              <p>{t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.STUDENTS.BODY.LIST_GROUP.DECLINED.TOOLTIP_P3')}</p>
            </Tooltip>
          }
        />
        {declinedMembers.length === 0 && (
          <List.Item title={t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.STUDENTS.BODY.LIST_GROUP.DECLINED.EMPTY')} />
        )}
        {declinedMembers.map(member => (
          <List.Item
            key={member.id}
            title={member.primaryEmail}
            actionArea={
              <Button
                prominence="neutral"
                size="sm"
                aria-label={t('PAGES.MY_GROUPS.LIST_GROUP.STUDENTS.DECLINED.REMOVE.A11Y_LABEL')}
                onClick={() => handleShowDeleteMemberModal(member)}
                startIcon={<Trash size="sm" />}
              />
            }
          />
        ))}
      </List>
    </ListGroupComponent>
  );
}

export default ListGroup;
